import React from "react";
import { X, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

/**
 * InvoiceCorrectionModal - Modal dialog for submitting invoice billing
 * corrections (company name, address, VAT, language, notes).
 *
 * @param {Object} props - Component props.
 * @param {Object} props.selectedInvoice - The invoice being corrected.
 * @param {Function} props.setSelectedInvoice - Closes the modal when set to null.
 * @param {Object} props.correctionForm - Correction form state.
 * @param {Function} props.setCorrectionForm - Updates correction form fields.
 * @param {Function} props.handleCorrectionSubmit - Submits the correction request.
 * @param {boolean} props.correctionSuccess - Whether submission succeeded.
 * @param {string} props.correctionError - Error message to display.
 * @param {boolean} props.isSubmittingCorrection - Submitting loading state.
 * @param {Function} props.t - i18n translation function.
 * @returns {React.ReactElement} The rendered correction modal.
 */
/** One guest invoice row (Supabase guest_invoices shape subset). */
interface GuestPortalInvoice {
  id: string;
  invoiceNumber?: string | null;
  [key: string]: unknown;
}

/** Correction form state owned by GuestPortalLayout. */
interface GuestPortalCorrectionForm {
  originalCompanyName: string;
  correctedCompanyName: string;
  street: string;
  zipCode: string;
  city: string;
  vatNumber: string;
  notes: string;
  languageCode: string;
}

interface InvoiceCorrectionModalProps {
  selectedInvoice: GuestPortalInvoice;
  setSelectedInvoice: (invoice: GuestPortalInvoice | null) => void;
  correctionForm: GuestPortalCorrectionForm;
  setCorrectionForm: (form: GuestPortalCorrectionForm) => void;
  handleCorrectionSubmit: (e: React.FormEvent) => void;
  correctionSuccess: boolean;
  correctionError: string;
  isSubmittingCorrection: boolean;
  t: (key: string) => string;
}

export default function InvoiceCorrectionModal({
  selectedInvoice,
  setSelectedInvoice,
  correctionForm,
  setCorrectionForm,
  handleCorrectionSubmit,
  correctionSuccess,
  correctionError,
  isSubmittingCorrection,
  t,
}: InvoiceCorrectionModalProps) {
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 animate-in fade-in">
      <div className="draggable-modal w-full max-w-lg bg-surface border border-border rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="px-6 py-5 bg-bg border-b border-border flex justify-between items-center">
          <div>
            <h3 className="font-black display-font uppercase text-sm tracking-wider">
              {t("guestPortal.requestCorrection")}
            </h3>
            <p className="text-[10px] text-muted font-black uppercase tracking-wider mt-0.5">
              {t("guestPortal.invoiceLabel")} {selectedInvoice.invoiceNumber || selectedInvoice.id}
            </p>
          </div>
          <button
            onClick={() => setSelectedInvoice(null)}
            className="p-1.5 hover:bg-slate-200 rounded-lg text-muted"
          >
            <X size={16} />
          </button>
        </div>

        {/* Modal Scrollable Content */}
        <div className="p-6 overflow-y-auto space-y-4 max-h-[60vh]">
          {correctionSuccess ? (
            <div className="text-center py-10 space-y-3">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full border border-emerald-100 animate-bounce">
                <CheckCircle size={36} />
              </div>
              <h4 className="font-black display-font text-base text-fg uppercase tracking-wider">
                {t("guestPortal.requestSubmitted")}
              </h4>
              <p className="text-xs text-muted max-w-xs mx-auto">
                {t("guestPortal.requestSubmittedDescription")}
              </p>
            </div>
          ) : (
            <form onSubmit={handleCorrectionSubmit} className="space-y-4 text-xs">
              {correctionError && (
                <div className="p-3.5 bg-rose-50 border border-rose-100 text-rose-500 rounded-xl flex gap-2 font-bold">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{correctionError}</span>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="font-black uppercase text-[9px] tracking-wider text-muted">
                    {t("guestPortal.previousName")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("guestPortal.previousNamePlaceholder")}
                    value={correctionForm.originalCompanyName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCorrectionForm({ ...correctionForm, originalCompanyName: e.target.value })}
                    className="w-full text-xs"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="font-black uppercase text-[9px] tracking-wider text-muted">
                    {t("guestPortal.futureName")}
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={t("guestPortal.futureNamePlaceholder")}
                    value={correctionForm.correctedCompanyName}
                    onChange={(e) => setCorrectionForm({ ...correctionForm, correctedCompanyName: e.target.value })}
                    className="w-full text-xs"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="font-black uppercase text-[9px] tracking-wider text-muted">
                    {t("guestPortal.street")}
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={t("guestPortal.streetPlaceholder")}
                    value={correctionForm.street}
                    onChange={(e) => setCorrectionForm({ ...correctionForm, street: e.target.value })}
                    className="w-full text-xs"
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col gap-1 col-span-1">
                    <label className="font-black uppercase text-[9px] tracking-wider text-muted">
                      {t("guestPortal.zipCode")}
                    </label>
                    <input
                      type="text"
                      required
                      placeholder={t("guestPortal.zipCodePlaceholder")}
                      value={correctionForm.zipCode}
                      onChange={(e) => setCorrectionForm({ ...correctionForm, zipCode: e.target.value })}
                      className="w-full text-xs"
                    />
                  </div>
                  <div className="flex flex-col gap-1 col-span-2">
                    <label className="font-black uppercase text-[9px] tracking-wider text-muted">
                      {t("guestPortal.city")}
                    </label>
                    <input
                      type="text"
                      required
                      placeholder={t("guestPortal.cityPlaceholder")}
                      value={correctionForm.city}
                      onChange={(e) => setCorrectionForm({ ...correctionForm, city: e.target.value })}
                      className="w-full text-xs"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="font-black uppercase text-[9px] tracking-wider text-muted">
                    {t("guestPortal.vatNumber")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("guestPortal.vatNumberPlaceholder")}
                    value={correctionForm.vatNumber}
                    onChange={(e) => setCorrectionForm({ ...correctionForm, vatNumber: e.target.value })}
                    className="w-full text-xs"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="font-black uppercase text-[9px] tracking-wider text-muted">
                    {t("guestPortal.invoiceLanguage")}
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCorrectionForm({ ...correctionForm, languageCode: "de" })}
                      className={`flex-1 py-2 text-xs font-bold rounded-xl border transition-all ${correctionForm.languageCode === "de" ? "bg-accent text-white border-accent shadow-md" : "bg-surface text-muted border-border hover:bg-bg"}`}
                    >
                      {t("guestPortal.german")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCorrectionForm({ ...correctionForm, languageCode: "en" })}
                      className={`flex-1 py-2 text-xs font-bold rounded-xl border transition-all ${correctionForm.languageCode === "en" ? "bg-accent text-white border-accent shadow-md" : "bg-surface text-muted border-border hover:bg-bg"}`}
                    >
                      {t("guestPortal.english")}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="font-black uppercase text-[9px] tracking-wider text-muted">
                    {t("guestPortal.additionalNotes")}
                  </label>
                  <textarea
                    rows={3}
                    placeholder={t("guestPortal.additionalNotesPlaceholder")}
                    value={correctionForm.notes}
                    onChange={(e) => setCorrectionForm({ ...correctionForm, notes: e.target.value })}
                    className="w-full text-xs font-sans"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-border flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedInvoice(null)}
                  className="px-4 py-2 border border-border text-[10px] font-black uppercase tracking-wider rounded-xl hover:bg-slate-50 cursor-pointer"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCorrection}
                  className="px-4 py-2 bg-accent text-white text-[10px] font-black uppercase tracking-wider rounded-xl hover:opacity-90 transition-all flex items-center gap-1.5 cursor-pointer shadow-lg shadow-lime-600/10"
                >
                  {isSubmittingCorrection && <Loader2 size={12} className="animate-spin" />}
                  {t("guestPortal.submitCorrection")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
